import { useTranslation } from 'react-i18next'
import React, { useState, useEffect } from 'react'
import styles from './EmployeeArchive.module.scss'
import {
  getEmployees,
  getEmployeeCases,
  getEmployeeCaseDetail,
  createEmployeeCase,
  createEmployeeDocument,
  uploadEmployeeDocumentFile,
  updateEmployeeCase,
  deleteEmployeeCase,
  updateEmployeeDocument,
  deleteEmployeeDocument,
} from '../../../service/ArchiveService'
import { Skeleton } from 'components/UnvUI'
import Notification from '../../../utils/Notifications'

const ICO_EMP = <svg viewBox="0 0 24 24"><path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/></svg>
const ICO_DOC = <svg viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
const ICO_PLUS = <svg viewBox="0 0 24 24"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
const ICO_DEL = <svg viewBox="0 0 24 24"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
const ICO_EDIT = <svg viewBox="0 0 24 24"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
const ICO_DL = <svg viewBox="0 0 24 24"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
const ICO_SEARCH = <svg viewBox="0 0 24 24"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>

const documentTypes = [
  { value: 'employment_order', label: 'Приказ о приёме' },
  { value: 'labor_contract', label: 'Трудовой договор' },
  { value: 'certification', label: 'Аттестация' },
  { value: 'award', label: 'Поощрение/Награда' },
  { value: 'salary_sheet', label: 'Ведомость зарплаты' },
  { value: 'vacation_sheet', label: 'Приказ об отпуске' },
  { value: 'disciplinary_action', label: 'Взыскание' },
  { value: 'memo', label: 'Докладная записка' },
  { value: 'other', label: 'Прочее' },
]

const getDocTypeLabel = (v) => documentTypes.find(t => t.value === v)?.label || v
const getConfLabel = (v) => ({ public: 'Открытый', internal: 'Внутренний', confidential: 'Конфиденц.', secret: 'Секретный' })[v] || v
const getConfClass = (v) => ({ public: styles.conf_public, internal: styles.conf_internal, confidential: styles.conf_confidential, secret: styles.conf_secret })[v] || ''
const fmtDate = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('ru-RU') } catch { return d } }

function EmployeeArchive() {
  const { t } = useTranslation('common')

  const [cases, setCases] = useState([])
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCase, setSelectedCase] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDocModal, setShowDocModal] = useState(false)
  const [editingDoc, setEditingDoc] = useState(null)
  const [uploadFile, setUploadFile] = useState(null)
  const [caseForm, setCaseForm] = useState({ employee: '', case_number: '', title: '', status: 'active', notes: '' })
  const [docForm, setDocForm] = useState({ document_type: 'employment_order', title: '', description: '', document_date: '', pages: 0, confidentiality_level: 'public', notes: '' })
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '', sound: '' })

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [c, e] = await Promise.all([getEmployeeCases(), getEmployees()])
      setCases(c); setEmployees(e)
    } catch { showNotify(t('auto.employeearchive.err_load', 'Ошибка при загрузке данных'), 'error') }
    finally { setLoading(false) }
  }

  const showNotify = (message, type) => setNotify({ isOpen: true, message, type, sound: type })

  const employeesWithoutCases = employees.filter(emp =>
    !cases.some(c => (c.employee === emp.id || c.employee?.id === emp.id))
  )

  const handleSelectCase = async (caseItem) => {
    if (selectedCase?.id === caseItem.id) { setSelectedCase(null); return }
    try {
      const detail = await getEmployeeCaseDetail(caseItem.id)
      setSelectedCase(detail)
    } catch {
      setSelectedCase(caseItem)
    }
  }

  const handleCreateCase = async (e) => {
    e.preventDefault()
    try {
      const n = await createEmployeeCase(caseForm)
      setCases([...cases, n])
      setCaseForm({ employee: '', case_number: '', title: '', status: 'active', notes: '' })
      setShowCreateModal(false)
      showNotify(t('auto.employeearchive.case_created', 'Дело сотрудника создано!'), 'success')
    } catch { showNotify(t('auto.employeearchive.err_create_case', 'Ошибка при создании дела'), 'error') }
  }

  const handleDeleteCase = async (e, caseId) => {
    e.stopPropagation()
    if (!window.confirm(t('auto.employeearchive.confirm_delete_case', 'Удалить дело сотрудника?'))) return
    try {
      await deleteEmployeeCase(caseId)
      setCases(cases.filter(c => c.id !== caseId))
      if (selectedCase?.id === caseId) setSelectedCase(null)
      showNotify(t('auto.employeearchive.case_deleted', 'Дело удалено'), 'success')
    } catch { showNotify(t('auto.employeearchive.err_delete_case', 'Ошибка при удалении дела'), 'error') }
  }

  const handleDigitizeCase = async (e, caseItem) => {
    e.stopPropagation()
    try {
      const upd = await updateEmployeeCase(caseItem.id, { is_digitized: !caseItem.is_digitized })
      setCases(cases.map(c => c.id === caseItem.id ? { ...c, ...upd } : c))
      if (selectedCase?.id === caseItem.id) setSelectedCase(prev => ({ ...prev, ...upd }))
    } catch { showNotify(t('auto.employeearchive.err_generic', 'Ошибка'), 'error') }
  }

  const openCreateDoc = () => {
    setEditingDoc(null)
    setDocForm({ document_type: 'employment_order', title: '', description: '', document_date: '', pages: 0, confidentiality_level: 'public', notes: '' })
    setUploadFile(null)
    setShowDocModal(true)
  }

  const openEditDoc = (doc) => {
    setEditingDoc(doc)
    setDocForm({ document_type: doc.document_type, title: doc.title, description: doc.description || '', document_date: doc.document_date || '', pages: doc.pages || 0, confidentiality_level: doc.confidentiality_level || 'public', notes: doc.notes || '' })
    setShowDocModal(true)
  }

  const handleCreateDocument = async (e) => {
    e.preventDefault()
    try {
      const data = { ...docForm, case: selectedCase.id }
      const n = await createEmployeeDocument(data, uploadFile)
      const updDocs = [...(selectedCase.documents || []), n]
      setSelectedCase({ ...selectedCase, documents: updDocs })
      setCases(cases.map(c => c.id === selectedCase.id ? { ...c, documents: updDocs } : c))
      setDocForm({ document_type: 'employment_order', title: '', description: '', document_date: '', pages: 0, confidentiality_level: 'public', notes: '' })
      setUploadFile(null)
      setShowDocModal(false)
      showNotify(t('auto.employeearchive.doc_added', 'Документ добавлен!'), 'success')
    } catch { showNotify(t('auto.employeearchive.err_add_doc', 'Ошибка при добавлении документа'), 'error') }
  }

  const handleUpdateDocument = async (e) => {
    e.preventDefault()
    try {
      const upd = await updateEmployeeDocument(editingDoc.id, docForm)
      const updDocs = (selectedCase.documents || []).map(d => d.id === editingDoc.id ? upd : d)
      setSelectedCase({ ...selectedCase, documents: updDocs })
      setCases(cases.map(c => c.id === selectedCase.id ? { ...c, documents: updDocs } : c))
      setEditingDoc(null); setShowDocModal(false)
      showNotify(t('auto.employeearchive.doc_updated', 'Документ обновлён!'), 'success')
    } catch { showNotify(t('auto.employeearchive.err_upd_doc', 'Ошибка при обновлении документа'), 'error') }
  }

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm(t('auto.employeearchive.confirm_delete_doc', 'Удалить документ?'))) return
    try {
      await deleteEmployeeDocument(docId)
      const updDocs = (selectedCase.documents || []).filter(d => d.id !== docId)
      setSelectedCase({ ...selectedCase, documents: updDocs })
      setCases(cases.map(c => c.id === selectedCase.id ? { ...c, documents: updDocs } : c))
      showNotify(t('auto.employeearchive.doc_deleted', 'Документ удалён'), 'success')
    } catch { showNotify(t('auto.employeearchive.err_del_doc', 'Ошибка при удалении документа'), 'error') }
  }

  const filteredCases = cases.filter(c => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    const empName = (c.employee_full_name || c.employee?.full_name || c.employee?.employee_full_name || '').toLowerCase()
    const empId = (c.employee_id || c.employee?.employee_id || '').toLowerCase()
    const dept = (c.department || c.employee?.department || '').toLowerCase()
    return empName.includes(q) || empId.includes(q) || dept.includes(q) || (c.case_number || '').toLowerCase().includes(q)
  })

  if (loading) {
    return <div className={styles.loading_container}><Skeleton variant="table" rows={5} cols={4} /></div>
  }

  return (
    <>
      <Notification notify={notify} setNotify={setNotify} />

      {showCreateModal && (
        <div className={styles.modal_overlay} onClick={() => setShowCreateModal(false)}>
          <div className={styles.modal_box} onClick={e => e.stopPropagation()}>
            <button className={styles.modal_close} onClick={() => setShowCreateModal(false)}>×</button>
            <h2>{t('archive.create_employee_case_modal')}</h2>
            <form onSubmit={handleCreateCase}>
              <div className={styles.form_field}>
                <label>{t('archive.field_employee_req')}</label>
                <select required value={caseForm.employee} onChange={e => setCaseForm({ ...caseForm, employee: e.target.value })}>
                  <option value="">{t('archive.select_employee_placeholder')}</option>
                  {employeesWithoutCases.length === 0
                    ? employees.map(emp => <option key={emp.id} value={emp.id}>{emp.full_name || emp.employee_full_name || emp.id}</option>)
                    : employeesWithoutCases.map(emp => <option key={emp.id} value={emp.id}>{emp.full_name || emp.employee_full_name || emp.id}</option>)
                  }
                </select>
              </div>
              <div className={styles.form_row}>
                <div className={styles.form_field}>
                  <label>{t('archive.field_case_no_req')}</label>
                  <input type="text" placeholder={t('archive.ph_case_no')} required value={caseForm.case_number}
                    onChange={e => setCaseForm({ ...caseForm, case_number: e.target.value })} />
                </div>
                <div className={styles.form_field}>
                  <label>{t('common.status')}</label>
                  <select value={caseForm.status} onChange={e => setCaseForm({ ...caseForm, status: e.target.value })}>
                    <option value="active">{t('archive.status_active')}</option>
                    <option value="closed">{t('archive.status_closed')}</option>
                    <option value="inactive">{t('archive.status_inactive')}</option>
                  </select>
                </div>
              </div>
              <div className={styles.form_field}>
                <label>{t('common.name')}</label>
                <input type="text" placeholder={t('archive.ph_personal_case')} value={caseForm.title}
                  onChange={e => setCaseForm({ ...caseForm, title: e.target.value })} />
              </div>
              <div className={styles.form_field}>
                <label>{t('archive.field_notes')}</label>
                <textarea placeholder={t('archive.ph_notes')} value={caseForm.notes}
                  onChange={e => setCaseForm({ ...caseForm, notes: e.target.value })} />
              </div>
              <div className={styles.form_actions}>
                <button type="submit" className={styles.btn_save}>{t('archive.btn_create_case')}</button>
                <button type="button" className={styles.btn_cancel} onClick={() => setShowCreateModal(false)}>{t('auto.employeearchive.cancel', 'Отмена')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDocModal && (
        <div className={styles.modal_overlay} onClick={() => setShowDocModal(false)}>
          <div className={styles.modal_box} onClick={e => e.stopPropagation()}>
            <button className={styles.modal_close} onClick={() => setShowDocModal(false)}>×</button>
            <h2>{editingDoc ? t('auto.employeearchive.edit_doc', 'Редактирование документа') : t('auto.employeearchive.add_doc', 'Добавление документа')}</h2>
            <form onSubmit={editingDoc ? handleUpdateDocument : handleCreateDocument}>
              <div className={styles.form_field}>
                <label>{t('archive.field_doc_type_req')}</label>
                <select required value={docForm.document_type} onChange={e => setDocForm({ ...docForm, document_type: e.target.value })}>
                  {documentTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className={styles.form_field}>
                <label>{t('archive.field_title_req')}</label>
                <input type="text" placeholder={t('archive.ph_doc_title')} required value={docForm.title}
                  onChange={e => setDocForm({ ...docForm, title: e.target.value })} />
              </div>
              <div className={styles.form_field}>
                <label>{t('assignments.description_label')}</label>
                <textarea placeholder={t('archive.ph_short_description')} value={docForm.description}
                  onChange={e => setDocForm({ ...docForm, description: e.target.value })} />
              </div>
              <div className={styles.form_row}>
                <div className={styles.form_field}>
                  <label>{t('archive.field_doc_date_req')}</label>
                  <input type="date" required value={docForm.document_date}
                    onChange={e => setDocForm({ ...docForm, document_date: e.target.value })} />
                </div>
                <div className={styles.form_field}>
                  <label>{t('archive.field_pages')}</label>
                  <input type="number" placeholder="0" value={docForm.pages}
                    onChange={e => setDocForm({ ...docForm, pages: parseInt(e.target.value) || 0 })} />
                </div>
              </div>
              <div className={styles.form_field}>
                <label>{t('archive.field_confidentiality')}</label>
                <select value={docForm.confidentiality_level} onChange={e => setDocForm({ ...docForm, confidentiality_level: e.target.value })}>
                  <option value="public">{t('archive.conf_public')}</option>
                  <option value="internal">{t('archive.conf_internal')}</option>
                  <option value="confidential">{t('archive.conf_confidential_full')}</option>
                  <option value="secret">{t('archive.conf_secret')}</option>
                </select>
              </div>
              <div className={styles.form_field}>
                <label>{t('archive.field_notes')}</label>
                <textarea placeholder={t('archive.ph_notes')} value={docForm.notes}
                  onChange={e => setDocForm({ ...docForm, notes: e.target.value })} />
              </div>
              {!editingDoc && (
                <div className={styles.form_field}>
                  <label>{t('archive.field_doc_file')}</label>
                  <input type="file" onChange={e => setUploadFile(e.target.files[0])} />
                </div>
              )}
              <div className={styles.form_actions}>
                <button type="submit" className={styles.btn_save}>{editingDoc ? t('auto.employeearchive.save', 'Сохранить') : t('auto.employeearchive.add', 'Добавить')}</button>
                <button type="button" className={styles.btn_cancel} onClick={() => setShowDocModal(false)}>Отмена</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className={styles.employee_archive}>
        <div className={styles.arch_header}>
          <div className={styles.arch_header_icon}>{ICO_EMP}</div>
          <div className={styles.arch_header_text}>
            <h1>{t('archive.card_employees')}</h1>
            <p>{t('archive.employees_archive_desc')}</p>
          </div>
          <button className={styles.btn_create} onClick={() => setShowCreateModal(true)}>{ICO_PLUS} {t('auto.employeearchive.create_case', 'Создать дело')}</button>
        </div>

        <div className={styles.search_bar}>
          {ICO_SEARCH}
          <input
            className={styles.search_input}
            type="text"
            placeholder={t('archive.search_employees_placeholder')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        <div className={styles.table_section}>
          <div className={styles.section_head}>
            <h2>{ICO_EMP} {t('auto.employeearchive.cases_title', 'Дела сотрудников')} ({filteredCases.length})</h2>
            <button className={styles.btn_create} onClick={() => setShowCreateModal(true)}>{ICO_PLUS} {t('auto.employeearchive.create_case', 'Создать дело')}</button>
          </div>
          <table className={styles.data_table}>
            <thead>
              <tr>
                <th>{t('archive.col_employee_full_name')}</th><th>{t('archive.col_employee_no')}</th><th>{t('archive.col_position')}</th><th>{t('archive.col_department')}</th><th>{t('archive.col_case_no')}</th><th>{t('common.status')}</th><th>{t('archive.col_digitized')}</th><th>{t('archive.col_documents')}</th><th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredCases.length === 0 ? (
                <tr className={styles.empty_row}><td colSpan={9}>
                  {searchQuery ? t('auto.employeearchive.no_match', 'Дела не найдены по вашему запросу.') : t('auto.employeearchive.no_cases', 'Нет дел сотрудников. Нажмите «Создать дело».')}
                </td></tr>
              ) : filteredCases.map(c => {
                const emp = c.employee
                const name = emp?.full_name || emp?.employee_full_name || c.employee_full_name || '—'
                const empId = emp?.employee_id || c.employee_id || '—'
                const position = emp?.position || c.position || '—'
                const dept = emp?.department || c.department || '—'
                return (
                  <tr key={c.id} className={selectedCase?.id === c.id ? styles.row_selected : ''}
                    onClick={() => handleSelectCase(c)}>
                    <td className={styles.td_name}>{name}</td>
                    <td className={styles.td_id}>{empId}</td>
                    <td className={styles.td_muted}>{position}</td>
                    <td className={styles.td_muted}>{dept}</td>
                    <td className={styles.td_id}>{c.case_number}</td>
                    <td>
                      <span className={`${styles.status_badge} ${c.status === 'active' ? styles.status_active : c.status === 'closed' ? styles.status_closed : styles.status_inactive}`}>
                        {c.status === 'active' ? t('auto.employeearchive.active', 'Активно') : c.status === 'closed' ? t('auto.employeearchive.closed', 'Закрыто') : c.status}
                      </span>
                    </td>
                    <td className={styles.td_center}>
                      {c.is_digitized
                        ? <span className={styles.digitized_badge}>{t('auto.components_employeearchive.k1')}</span>
                        : <span style={{ color: '#AAAAAA', fontSize: '0.8rem', fontFamily: 'var(--font-base, Inter, sans-serif)' }}>{t('common.no')}</span>}
                    </td>
                    <td className={styles.td_center}>{(c.documents || []).length}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className={styles.td_actions}>
                        <button className={`${styles.btn_row} ${c.is_digitized ? styles.btn_row_blue : styles.btn_row_green}`}
                          onClick={e => handleDigitizeCase(e, c)}>
                          {c.is_digitized ? t('auto.employeearchive.undigitize', 'Снять') : t('auto.employeearchive.digitize', 'Оцифровать')}
                        </button>
                        <button className={`${styles.btn_row} ${styles.btn_row_red}`}
                          onClick={e => handleDeleteCase(e, c.id)}>{ICO_DEL}</button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {selectedCase && (
          <div className={styles.docs_section}>
            <div className={styles.docs_head}>
              <h2>{ICO_DOC} {t('auto.employeearchive.documents', 'Документы')} — {selectedCase.employee?.full_name || selectedCase.employee?.employee_full_name || selectedCase.employee_full_name || selectedCase.case_number}</h2>
              <button className={styles.btn_create} onClick={openCreateDoc}>{ICO_PLUS} {t('auto.employeearchive.add', 'Добавить')}</button>
            </div>
            <div className={styles.case_info_bar}>
              <div className={styles.info_chip}><span className={styles.info_chip_label}>{t('archive.ocr_col_case_no')}</span><span className={styles.info_chip_value}>{selectedCase.case_number}</span></div>
              {(selectedCase.employee?.position || selectedCase.position) && <div className={styles.info_chip}><span className={styles.info_chip_label}>{t('archive.col_position')}</span><span className={styles.info_chip_value}>{selectedCase.employee?.position || selectedCase.position}</span></div>}
              {(selectedCase.employee?.department || selectedCase.department) && <div className={styles.info_chip}><span className={styles.info_chip_label}>{t('archive.col_department')}</span><span className={styles.info_chip_value}>{selectedCase.employee?.department || selectedCase.department}</span></div>}
              <div className={styles.info_chip}><span className={styles.info_chip_label}>{t('archive.col_digitized')}</span><span className={styles.info_chip_value}>{selectedCase.is_digitized ? t('common.yes', 'Да') : t('common.no', 'Нет')}</span></div>
            </div>
            <table className={styles.data_table}>
              <thead>
                <tr>
                  <th>{t('archive.ocr_col_doc_type')}</th><th>{t('common.name')}</th><th>{t('assignments.description_label')}</th><th>{t('common.date')}</th><th>{t('archive.col_pages_short')}</th><th>{t('archive.conf_confidential')}</th><th>{t('common.files')}</th><th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {(!selectedCase.documents || selectedCase.documents.length === 0) ? (
                  <tr className={styles.empty_row}><td colSpan={8}>{t('archive.no_documents_add')}</td></tr>
                ) : selectedCase.documents.map(doc => (
                  <tr key={doc.id}>
                    <td><span className={styles.doc_type_badge}>{getDocTypeLabel(doc.document_type)}</span></td>
                    <td className={styles.td_name}>{doc.title}</td>
                    <td className={styles.td_muted}>{doc.description || '—'}</td>
                    <td>{fmtDate(doc.document_date)}</td>
                    <td className={styles.td_center}>{doc.pages > 0 ? doc.pages : '—'}</td>
                    <td><span className={`${styles.confidentiality_badge} ${getConfClass(doc.confidentiality_level)}`}>{getConfLabel(doc.confidentiality_level)}</span></td>
                    <td>
                      {doc.files?.length > 0 ? (
                        <div className={styles.file_links}>
                          {doc.files.map(f => (
                            <a key={f.id} href={f.file} download={f.file_name} className={styles.file_download_link}>
                              {ICO_DL} {f.file_name || t('auto.employeearchive.download', 'Скачать')}
                            </a>
                          ))}
                        </div>
                      ) : '—'}
                    </td>
                    <td>
                      <div className={styles.td_actions}>
                        <button className={`${styles.btn_row} ${styles.btn_row_dark}`} onClick={() => openEditDoc(doc)}>{ICO_EDIT} {t('auto.employeearchive.edit_short', 'Ред.')}</button>
                        <button className={`${styles.btn_row} ${styles.btn_row_red}`} onClick={() => handleDeleteDocument(doc.id)}>{ICO_DEL}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

export default EmployeeArchive
